// =====================================================================
// EGL v12 Frontend - CERTIFICATE LIVE PREVIEW
// Purpose: Real-time preview of certificate as user types in creation form
// =====================================================================

import React from 'react'
import './CertificateLivePreview.css'

interface CertificateLivePreviewProps {
  formData: {
    cert_id?: string
    metal_composition?: string
    item_gross_weight?: string
    gemstone_growth_origin?: string
    comment_line_1?: string
    comment_line_2?: string
    comment_line_3?: string
    abbreviation_line_1?: string
    abbreviation_line_2?: string
    abbreviation_line_3?: string
    abbreviation_line_4?: string
    abbreviation_line_5?: string
    abbreviation_line_6?: string
    abbreviation_line_7?: string
    abbreviation_line_8?: string
    abbreviation_line_9?: string
    abbreviation_line_10?: string
    abbreviation_line_11?: string
    abbreviation_line_12?: string
    abbreviation_line_13?: string
  }
  imageUpload?: {
    file: File | null
    uploaded: boolean
    s3Key: string | null
  }
}

export const CertificateLivePreview: React.FC<CertificateLivePreviewProps> = ({ 
  formData, 
  imageUpload 
}) => {
  // Filter comment lines to show only those with data
  const validCommentLines = [
    formData.comment_line_1,
    formData.comment_line_2,
    formData.comment_line_3
  ].filter(comment => comment && comment.trim() !== '')

  // Filter abbreviation lines to show only those with data
  const validAbbreviationLines = [
    formData.abbreviation_line_1,
    formData.abbreviation_line_2,
    formData.abbreviation_line_3,
    formData.abbreviation_line_4,
    formData.abbreviation_line_5,
    formData.abbreviation_line_6,
    formData.abbreviation_line_7,
    formData.abbreviation_line_8,
    formData.abbreviation_line_9,
    formData.abbreviation_line_10,
    formData.abbreviation_line_11,
    formData.abbreviation_line_12,
    formData.abbreviation_line_13
  ].filter(abbr => abbr && abbr.trim() !== '')

  // Generate image URL for preview
  const getImageUrl = () => {
    if (imageUpload?.uploaded && imageUpload.s3Key) {
      return `https://verifygold.s3.me-central-1.amazonaws.com/${imageUpload.s3Key}`
    }
    if (imageUpload?.file) {
      return URL.createObjectURL(imageUpload.file)
    }
    return null
  }

  const imageUrl = getImageUrl()

  return (
    <div className="live-preview-container">
      <div className="live-preview-header">
        <h3>Live Preview</h3>
        <span className="preview-badge">Updates as you type</span>
      </div>
      
      <div className="certificate-preview">
        <div className="logo section">
          <img 
            src="https://verifygold.s3.me-central-1.amazonaws.com/EGL/Logo/EGL_Logo.svg" 
            alt="EGL Logo" 
          />
        </div>

        {/* Certificate ID */}
        {formData.cert_id && (
          <div className="section field">
            <strong>CERTIFICATE ID</strong>
            <span>{formData.cert_id}</span>
          </div>
        )}

        {/* Item Gross Weight */}
        {formData.item_gross_weight && (
          <div className="section field">
            <strong>ITEM GROSS WEIGHT</strong>
            <span>{parseFloat(formData.item_gross_weight).toFixed(2)} grams</span>
          </div>
        )}

        {/* Metal Composition */}
        {formData.metal_composition && (
          <div className="section field">
            <strong>METAL COMPOSITION</strong>
            <span>{formData.metal_composition}</span>
          </div>
        )}

        {/* Gemstone Growth Origin */}
        {formData.gemstone_growth_origin && (
          <div className="section field">
            <strong>GEMSTONE GROWTH ORIGINS</strong>
            <span>{formData.gemstone_growth_origin}</span>
          </div>
        )}

        {/* Gemstone Specifications (Comment Lines) */}
        {validCommentLines.length > 0 && (
          <div className="section gemstone-specifications">
            <strong>GEMSTONE SPECIFICATIONS</strong>
            <div className="gemstone-specs-content">
              {validCommentLines.map((comment, index) => (
                <div key={index} className="gemstone-spec-line">{comment}</div>
              ))}
            </div>
          </div>
        )}

        {/* Authentication & Grading Info Block */}
        <div className="info-block section">
          <strong>Authentication & Grading:</strong>
          Authentication, grading, and origin were determined using non-invasive methods, as permitted by the item's mounting. Abbreviations marked with * are defined below.
        </div>

        {/* Image */}
        <div className="image-box section">
          {imageUrl ? (
            <img src={imageUrl} alt="Certificate" />
          ) : (
            <div className="no-image">
              <span>Upload an image to see preview</span>
            </div>
          )}
        </div>

        {/* Abbreviations */}
        {validAbbreviationLines.length > 0 && (
          <div className="abbreviations section">
            <h3>ABBREVIATION(S)</h3>
            <div className="abbreviation-lines">
              {validAbbreviationLines.map((abbr, index) => (
                <div key={index}>{abbr}</div>
              ))}
            </div>
          </div>
        )}

        {/* Report Conditions Info Block */}
        <div className="info-block section">
          <strong>Report Conditions:</strong>
          This report is not a guarantee, valuation, or appraisal. It reflects the characteristics of the article at the time of examination using methods and equipment available to EUROPEAN GEMOLOGICAL LAB LTD. Results may vary with different examiners, conditions, or technological advances. EUROPEAN GEMOLOGICAL LAB LTD accepts no liability for errors, omissions, or use of this report or any inscription, including indirect or consequential damages. Only the original report, accessible via the QR CODE on the front of the original card certificate, reflects the most current grading information. PDF, shared, or printed copies are not authoritative.
        </div>

        {/* Footer */}
        <div className="footer section">
          71–75 Shelton Street, Covent Garden<br />
          London WC2H 9JQ, England, United Kingdom (UK)<br />
          &copy; 2025 European Gemological Lab Ltd. | <a href="https://www.eugemlab.com">www.eugemlab.com</a>
        </div>
      </div>
    </div>
  )
} 